import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  requestLightningQuote,
  submitLightningPayout,
  type LightningQuoteResponse,
} from '../api'
import { loadToken, type DecodedToken } from '../_shared/token'
import {
  Badge,
  Button,
  Card,
  ErrorPanel,
  Field,
  Footer,
  FullPageLoader,
  Header,
  Page,
  TextInput,
} from '../_shared/ui'

// LIGHTNING address detection — same shape the Edge Function uses.
const LIGHTNING_ADDRESS_RE = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const BOLT11_RE = /^lnbc[0-9a-z]{50,}$/i

type InputKind = 'address' | 'invoice'

function formatSats(n: number): string {
  return new Intl.NumberFormat('en-GB').format(n)
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

export default function LightningForm() {
  const { surveyId = '' } = useParams()
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const [token, setToken] = useState<string | null>(null)
  const [decoded, setDecoded] = useState<DecodedToken | null>(null)
  const [bootstrapped, setBootstrapped] = useState(false)

  const [kind, setKind] = useState<InputKind>('address')
  const [input, setInput] = useState('')
  const [consent, setConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<{ input?: string; consent?: string }>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  const [quote, setQuote] = useState<LightningQuoteResponse | null>(null)
  const [quoteError, setQuoteError] = useState<string | null>(null)
  const [quoting, setQuoting] = useState(false)

  // 1-second tick for countdown
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 1000)
    return () => window.clearInterval(id)
  }, [])

  useEffect(() => {
    const { token: t, decoded: d } = loadToken(surveyId, search.toString())
    setToken(t)
    setDecoded(d)
    setBootstrapped(true)
  }, [surveyId, search])

  const fetchQuote = useCallback(() => {
    if (!token || !decoded) return
    setQuoting(true)
    setQuoteError(null)
    requestLightningQuote({
      surveyId,
      panellistToken: token,
      tier: decoded.tier,
    }).then((res) => {
      setQuoting(false)
      if (!res.ok) {
        setQuote(null)
        setQuoteError(humanError(res.error))
        return
      }
      setQuote(res.data)
    })
  }, [surveyId, token, decoded])

  // Initial quote on page load
  useEffect(() => {
    if (bootstrapped && token && decoded && !quote && !quoting && !quoteError) {
      fetchQuote()
    }
  }, [bootstrapped, token, decoded, quote, quoting, quoteError, fetchQuote])

  const expiresAtMs = quote ? new Date(quote.expires_at).getTime() : 0
  const secondsLeft = quote ? Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000)) : 0
  const expired = !!quote && secondsLeft <= 0

  const inputDetected: InputKind | null = useMemo(() => {
    const v = input.trim()
    if (!v) return null
    if (BOLT11_RE.test(v)) return 'invoice'
    if (LIGHTNING_ADDRESS_RE.test(v)) return 'address'
    return null
  }, [input])

  if (!bootstrapped) return <FullPageLoader />

  if (!token || !decoded || decoded.expired) {
    return (
      <Page>
        <Header />
        <ErrorPanel
          title="Session expired"
          body="Please reopen the payment link your sponsor sent you."
        />
        <Footer surveyId={surveyId} />
      </Page>
    )
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!quote || expired) {
      setSubmitError('The 60-second rate lock expired. Get a fresh quote and try again.')
      return
    }
    const errs: typeof fieldErrors = {}
    const v = input.trim()
    if (!v) errs.input = 'Required'
    else if (kind === 'address' && !LIGHTNING_ADDRESS_RE.test(v)) {
      errs.input = 'Must look like you@walletofsatoshi.com'
    } else if (kind === 'invoice' && !BOLT11_RE.test(v)) {
      errs.input = 'Must be a BOLT11 invoice starting with lnbc...'
    }
    if (!consent) errs.consent = 'You must consent to submit'
    setFieldErrors(errs)
    if (Object.keys(errs).length > 0) return

    setSubmitting(true)
    setSubmitError(null)
    submitLightningPayout({
      surveyId,
      panellistToken: token!,
      tier: decoded!.tier,
      quoteId: quote.quote_id,
      addressOrInvoice: v,
      consent,
    }).then((res) => {
      setSubmitting(false)
      if (!res.ok) {
        // If the quote expired between locked render and submit, give the
        // panellist a one-click way to recover instead of a dead end.
        if (res.error === 'quote_expired') {
          setQuote(null)
          fetchQuote()
        }
        setSubmitError(humanError(res.error))
        return
      }
      navigate(`/p/${surveyId}/confirm`, { state: res.data })
    })
  }

  return (
    <Page>
      <Header
        surveyName="Bitcoin Lightning"
        sponsorName={`Tier: ${decoded.tier}`}
        badge={<Badge variant="info">Crypto · instant</Badge>}
      />

      <Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--cp-sp-3)' }}>
          <div style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-fg-muted)' }}>
            Locked rate
          </div>
          {!quote && quoting && (
            <div className="cp-tabular" style={{ fontSize: 'var(--cp-text-md)', color: 'var(--cp-fg-muted)' }}>
              Fetching BTC/EUR rate…
            </div>
          )}
          {!quote && quoteError && (
            <div
              style={{
                background: 'var(--cp-danger-bg)',
                color: 'var(--cp-danger-dark)',
                borderRadius: 'var(--cp-radius-md)',
                padding: '10px 12px',
                fontSize: 'var(--cp-text-sm)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <span>{quoteError}</span>
              <Button variant="ghost" onClick={fetchQuote} disabled={quoting}>
                Retry
              </Button>
            </div>
          )}
          {quote && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div
                className="cp-tabular"
                style={{ fontSize: 'var(--cp-text-2xl)', fontWeight: 600 }}
              >
                €{(quote.amount_eur_cents / 100).toFixed(2)} ={' '}
                {formatSats(quote.sats)} sats
              </div>
              <div style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-fg-muted)' }}>
                Rate from {quote.fx_source} · 1 BTC ={' '}
                €{quote.fx_rate.toLocaleString('en-GB', { maximumFractionDigits: 0 })} ·{' '}
                {expired ? (
                  <span style={{ color: 'var(--cp-danger)' }}>expired</span>
                ) : (
                  <span>
                    locked {pad(Math.floor(secondsLeft / 60))}:{pad(secondsLeft % 60)}
                  </span>
                )}
              </div>
              {(expired || secondsLeft < 10) && (
                <div>
                  <Button variant="ghost" onClick={fetchQuote} disabled={quoting}>
                    Get a fresh quote
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      <form onSubmit={onSubmit}>
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--cp-sp-4)' }}>
            <Field
              label={kind === 'address' ? 'Lightning address' : 'Lightning invoice (BOLT11)'}
              hint={
                kind === 'address'
                  ? 'Looks like an email. Most modern Lightning wallets give you one — for example you@walletofsatoshi.com.'
                  : 'Paste a BOLT11 invoice for the exact sats shown above. Generate it in your wallet first.'
              }
              error={fieldErrors.input}
            >
              <TextInput
                value={input}
                onChange={setInput}
                placeholder={
                  kind === 'address' ? 'you@walletofsatoshi.com' : 'lnbc25u1p…'
                }
                autoComplete="off"
                spellCheck={false}
                maxLength={kind === 'invoice' ? 1024 : 128}
                invalid={!!input && inputDetected !== null && inputDetected !== kind}
              />
            </Field>

            <button
              type="button"
              onClick={() => {
                setKind((k) => (k === 'address' ? 'invoice' : 'address'))
                setInput('')
                setFieldErrors({})
              }}
              style={{
                alignSelf: 'flex-start',
                background: 'transparent',
                border: 'none',
                padding: 0,
                color: 'var(--cp-accent-primary)',
                fontSize: 'var(--cp-text-sm)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {kind === 'address'
                ? 'Use a Lightning invoice instead →'
                : 'Use a Lightning address instead →'}
            </button>

            <label
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                fontSize: 'var(--cp-text-sm)',
                color: 'var(--cp-fg-secondary)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>
                I confirm the Lightning destination above is mine and I accept the locked sats
                amount as full settlement of the EUR honorarium. I will receive a verifiable
                receipt.
              </span>
            </label>
            {fieldErrors.consent && (
              <span style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-danger)' }}>
                {fieldErrors.consent}
              </span>
            )}

            {submitError && (
              <div
                style={{
                  background: 'var(--cp-danger-bg)',
                  color: 'var(--cp-danger-dark)',
                  borderRadius: 'var(--cp-radius-md)',
                  padding: '10px 12px',
                  fontSize: 'var(--cp-text-sm)',
                }}
              >
                {submitError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" onClick={() => navigate(`/p/${surveyId}`)} disabled={submitting}>
                Back
              </Button>
              <Button type="submit" loading={submitting} fullWidth disabled={!quote || expired}>
                Confirm payout
              </Button>
            </div>
          </div>
        </Card>
      </form>

      <Footer surveyId={surveyId} />
    </Page>
  )
}

function humanError(err: string): string {
  switch (err) {
    case 'invalid_token': return 'Your payment link is no longer valid. Please contact your sponsor.'
    case 'token_survey_mismatch':
    case 'token_tier_mismatch':
    case 'token_quote_mismatch': return 'The payment link does not match this survey or tier.'
    case 'survey_not_accepting_payouts': return 'This survey is no longer accepting payouts.'
    case 'method_not_enabled': return 'Lightning is not enabled for this survey.'
    case 'already_submitted': return 'You have already submitted this payout. Check your email for the receipt.'
    case 'survey_not_configured': return 'This survey is not fully configured yet. Please contact the sponsor.'
    case 'consent_required': return 'You must check the consent box to submit.'
    case 'fx_unavailable': return 'BTC/EUR price source is temporarily unavailable. Try again in a moment.'
    case 'quote_expired': return 'The 60-second rate lock expired. A new quote has been requested.'
    case 'quote_already_used': return 'This quote has already been used. Please get a new one.'
    case 'quote_not_found': return 'Quote not found — it may have expired. Please get a new one.'
    case 'invalid_lightning_target': return 'Provide either a Lightning address (you@walletofsatoshi.com) or a BOLT11 invoice (lnbc...).'
    case 'payout_failed': return 'The payout failed. Double-check your address or invoice and try again, or use a different payment method.'
    default: return `Could not submit (${err}). Please try again or contact the sponsor.`
  }
}
